import { Injectable } from '@angular/core';
import { Filter, FilterConfig } from '../interfaces/filter.interface';
import { map, Observable, of } from 'rxjs';
import { DocumentSubType, getDocumentSubTypeLabels, PaymentType, PaymentTypeTranslations } from '../interfaces/invoice.interface';
import { ShiftStatus, ShiftStatusTranslations } from '../interfaces/shift.interface';
import { ZatcaInvoiceState, ZatcaInvoiceStateTranslations } from '../interfaces/zatca-invoice-state.interface';
import { LookupService } from 'src/app/shared/services/lookup.service';
import { PaginatedLookupRequest } from '../interfaces/lookup.interface';

/** Minimum account level for Lookup/Accounts in vouchers, journals, and warehouse orders. */
const ACCOUNTS_LEVEL_MIN_FOR_VOUCHERS_AND_JOURNALS = 5;

@Injectable({
  providedIn: 'root'
})
export class FiltersListService {

  constructor(
    private lookupService: LookupService
  ) { }

  getBranches(key = 'branchId'): FilterConfig {
    return {
      key,
      label: "Branch",
      type: "options",
      labelAr: "الفرع",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'Branches',
          category: 'ALL'
        };
        return this.lookupService.listBranches(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)))
      }
    };
  }

  getStores(key = 'storeId'): FilterConfig {
    return {
      key,
      label: "Store",
      type: "options",
      labelAr: "المستودع",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'Stores',
          category: 'ALL'
        };
        return this.lookupService.listStores(request)
          .pipe(map((res: any) => {
            const mapped = this.mapLookupResponse(res.data);
            return mapped.sort((a: any, b: any) => {
              if (a.meta && !b.meta) return -1;
              if (!a.meta && b.meta) return 1;
              return 0;
            });
          }))
      }
    };
  }

  getUsers(key = 'userId'): FilterConfig {
    return {
      key,
      label: "User",
      type: "options",
      labelAr: "المستخدم",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'Users',
          category: 'ALL'
        };
        return this.lookupService.listUsers(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)))
      }
    };
  }

  /** Multi-select creator filter; field name must match API (e.g. createdBy). */
  getCreatedByUsers(key = 'createdBy'): FilterConfig {
    return {
      ...this.getUsers(key),
      key,
      label: 'User',
      labelAr: 'المستخدم',
      multiSelect: true,
    };
  }

  /**
   * Users lookup for report filter dropdowns: large page size so the list behaves like a full DDL.
   */
  fetchReportCreatedByUsers = (
    pageNumber: number,
    _pageSize: number,
    term: string,
    advancedFilter: Filter[]
  ) =>
    this.getUsers('createdBy').optionsFnc!(
      pageNumber,
      10000,
      term,
      advancedFilter
    );

  getShiftCategories(key = 'categoryId'): FilterConfig {
    return {
      key,
      label: "Shift Category",
      type: "options",
      labelAr: "فئة الشفت",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'ShiftCategories',
          category: 'ALL'
        };
        return this.lookupService.listShiftCategories(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)))
      }
    };
  }

  getShiftStates(key = 'shiftStatusId'): FilterConfig {
    return {
      key,
      label: "Shift Status",
      type: "options",
      labelAr: "حالة الشفت",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'ShiftStatuses',
          category: 'ALL'
        };
        return this.lookupService.listShiftStatuses(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)))
      }
    };
  }

  getDocumentTypes(key = 'documentType'): FilterConfig {
    return {
      key,
      label: "Document",
      type: "options",
      labelAr: "الفاتورة",
      optionsFnc: (page, size, search, advancedFilter) => getDocumentSubTypeLabels()
        .pipe(map((res: any) => res.map((role: any) => ({
          key: role.id,
          value: role.labelEn,
          label: role.labelEn,
          labelAr: role.labelAr,
          id: role.id,
          nameEn: role.labelEn,
          nameAr: role.labelAr,
          name: role.labelEn
        })))
        )
    };
  }

  getRoles(key = 'roles'): FilterConfig {
    return {
      key,
      label: "Role",
      type: "options",
      labelAr: "الدور",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'Roles',
          category: 'ALL'
        };
        return this.lookupService.listRoles(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)))
      }
    };
  }

  getProductCategories(key = 'parentId'): FilterConfig {
    return {
      key,
      label: "Product Category",
      type: "options",
      labelAr: "فئة الصنف",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'ProductCategories',
          category: 'ALL'
        };
        return this.lookupService.listProductCategories(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)))
      }
    };
  }

  /**
   * Item nature (صنف / خدمة). Keys must match API `productType` and
   * `CreateOrUpdateProductComponent.fetchProductTypes` — not ProductGroups (فئات الأصناف).
   */
  getProductTypes(key = 'productType'): FilterConfig {
    return {
      key,
      label: 'Product type',
      type: 'options',
      labelAr: 'نوع الصنف',
      options: [
        { key: 'Product', label: 'Product', labelAr: 'صنف' },
        { key: 'Service', label: 'Service', labelAr: 'خدمة' },
      ],
    };
  }

  /**
   * Product group / item category (فئات الأصناف). Filters by `groupId` on product list APIs.
   */
  getProductGroups(key = 'groupId'): FilterConfig {
    return {
      key,
      label: 'Product groups',
      type: 'options',
      labelAr: 'فئات الأصناف',
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'ProductGroups',
          category: 'ALL',
        };
        return this.lookupService
          .listProductGroups(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)));
      },
    };
  }

  getCurreies(key = 'currencyId'): FilterConfig {
    return {
      key,
      label: 'Currency',
      type: 'options',
      labelAr: 'العملة',
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'Currencies',
          category: 'ALL'
        };
        return this.lookupService.listCurrencies(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)))
      }
    };
  }

  getAccountCategories(key = 'glAccountCategoryType'): FilterConfig {
    return {
      key,
      label: "Account Category",
      type: "options",
      labelAr: "تصنيف الحساب",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'AccountCategories',
          category: 'ALL'
        };
        return this.lookupService.listAccountCategories(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)))
      }
    }
  }

  getZatcaStatus(key = 'zatcaState'): FilterConfig {
    return {
      key,
      label: "Zatca State",
      type: "options",
      labelAr: "حالة الزكاة",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'ZatcaStates',
          category: 'ALL'
        };
        return this.lookupService.listZatcaStates(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)))
      }
    };
  }

  getJournalTypes(key = 'journalTypeId'): FilterConfig {
    return {
      key,
      label: "Journal Type",
      type: "options",
      labelAr: "نوع القيد",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'JournalTypes',
          category: 'ALL'
        };
        return this.lookupService.listJournalTypes(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)))
      }
    }
  }

  getFinancialStatementTypes(key = 'financialStatementItemType'): FilterConfig {
    return {
      key,
      label: "Financial Statement Types",
      type: "options",
      labelAr: "انواع البيان المالي",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'FinancialStatementItems',
          category: 'ALL'
        };
        return this.lookupService.listFinancialStatementItems(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)))
      }
    }
  }

  getCountries(key = 'countryCode'): FilterConfig {
    return {
      key,
      label: "Country",
      type: "options",
      labelAr: "الدولة",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'Countries',
          category: 'ALL'
        };
        return this.lookupService.listCountries(request)
          .pipe(map((res: any) => res.data.map((item: any) => ({
            ...item,
            key: item.meta ?? item.id,   // meta = countryCode (e.g. "SA"), fallback to id
            label: item.nameEn ?? item.nameAr,
            labelAr: item.nameAr ?? item.nameEn,
            id: item.id,
            nameEn: item.nameEn ?? item.nameAr,
            nameAr: item.nameAr ?? item.nameEn,
            name: item.nameEn ?? item.nameAr
          }))))
      }
    }
  }

  getFinancialYears(key = 'financialYearId'): FilterConfig {
    return {
      key,
      label: "Financial Year",
      type: "options",
      labelAr: "السنة المالية",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'FinancialYear',
          category: 'ALL'
        };
        return this.lookupService.listFinancialYears(request)
          .pipe(map((res: any) => this.mapFinancialYearsResponse(res.data)))
      }
    }
  }

  getPaymentTypes(key = 'PaymentType'): FilterConfig {
    return {
      key,
      label: "Payment Type",
      type: "options",
      labelAr: "نوع الدفع",
      optionsFnc: (page, size, search, advancedFilter) => this.fetchPaymentTypes(page, size, search)
        .pipe(map((res: any) => res.data.map((category: any) => ({
          key: category.type,
          label: category.type,
          labelAr: category.typeAr,
          id: category.type,
          nameEn: category.type,
          nameAr: category.typeAr
        })))
        )
    }
  }

  getShifts(key = 'shiftNo'): FilterConfig {
    return {
      key,
      label: "Shift",
      type: "options",
      labelAr: "الشفت",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'Shifts',
          category: 'ALL'
        };
        return this.lookupService.listShifts(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)))
      }
    };
  }

  getProducts(key = 'productId'): FilterConfig {
    return {
      key,
      label: "Product",
      type: "options",
      labelAr: "الصنف",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'Products',
          category: 'ALL'
        };
        return this.lookupService.listProducts(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)))
      }
    };
  }

  getProductsWithUnits(key = 'productId'): FilterConfig {
    return {
      key,
      label: "Product",
      type: "options",
      labelAr: "الصنف",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'ProductsWithUnits',
          category: 'ALL'
        };
        return this.lookupService.listProductsWithUnits(request)
          .pipe(map((res: any) => this.mapProductsWithUnitsResponse(res.data)))
      }
    };
  }

  getTaxCategories(key = 'taxCategoryId'): FilterConfig {
    return {
      key,
      label: "Tax Category",
      type: "options",
      labelAr: "فئة الضريبة",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'TaxCategories',
          category: 'ALL'
        };
        return this.lookupService.listTaxCategories(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)))
      }
    };
  }

  getTaxExceptions(key = 'taxExceptionId'): FilterConfig {
    return {
      key,
      label: "Tax Exception",
      type: "options",
      labelAr: "استثناء الضريبة",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'TaxExceptions',
          category: 'ALL'
        };
        return this.lookupService.listTaxExceptions(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)))
      }
    };
  }

  getUnits(key = 'unitId'): FilterConfig {
    return {
      key,
      label: "Unit",
      type: "options",
      labelAr: "الوحدة",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'Units',
          category: 'ALL'
        };
        return this.lookupService.listUnits(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)))
      }
    };
  }

  getAccounts(key = 'accountId'): FilterConfig {
    return {
      key,
      label: "Account",
      type: "options",
      labelAr: "الحساب",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'Accounts',
          category: 'ALL'
        };
        return this.lookupService.listAccounts(request)
          .pipe(map((res: any) => this.mapAccountLookupResponse(res.data)))
      }
    };
  }

  /**
   * Accounts lookup for vouchers, journals, and warehouse orders only.
   * Always applies isActive=true and level > ACCOUNTS_LEVEL_MIN_FOR_VOUCHERS_AND_JOURNALS; caller filters are merged.
   */
  getAccountsForVouchersAndJournals(key = 'accountId'): FilterConfig {
    const defaultFilters: Filter[] = [
      { logic: 'AND', field: 'isActive', operator: 'EQ', value: 'true' },
      { logic: 'AND', field: 'level', operator: 'GT', value: String(ACCOUNTS_LEVEL_MIN_FOR_VOUCHERS_AND_JOURNALS) }
    ];
    return {
      key,
      label: "Account",
      type: "options",
      labelAr: "الحساب",
      optionsFnc: (page, size, search, advancedFilter) => {
        const mergedFilters = [...defaultFilters, ...(advancedFilter || [])];
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: mergedFilters,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'Accounts',
          category: 'ALL'
        };
        return this.lookupService.listAccounts(request)
          .pipe(map((res: any) => this.mapAccountLookupResponse(res.data)));
      }
    };
  }

  getGlAccountWithCategories(key = 'glAccountId'): FilterConfig {
    return {
      key,
      label: "GL Account",
      type: "options",
      labelAr: "الحساب العام",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'GlAccountWithCategories',
          category: 'ALL'
        };
        return this.lookupService.listGlAccountWithCategories(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)))
      }
    };
  }

  getContacts(key = 'contactId'): FilterConfig {
    return {
      key,
      label: "Contact",
      type: "options",
      labelAr: "جهة الاتصال",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'Contacts',
          category: 'ALL'
        };
        return this.lookupService.listContacts(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)))
      }
    };
  }

  getCompanies(key = 'companyId'): FilterConfig {
    return {
      key,
      label: "Company",
      type: "options",
      labelAr: "الشركة",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'Companies',
          category: 'ALL'
        };
        return this.lookupService.listCompanies(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)))
      }
    };
  }

  getCustomers(key = 'customerId'): FilterConfig {
    return {
      key,
      label: "Customer",
      type: "options",
      labelAr: "العميل",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'Customers',
          category: 'ALL'
        };
        return this.lookupService.listCustomers(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)))
      }
    };
  }

  getPurchaseRequest(key = 'requestId'): FilterConfig {
    return {
      key,
      label: "Purchase Requisition",
      type: "options",
      labelAr: "طلب الشراء",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'PurchaseRequest',
          category: 'ALL'
        };
        return this.lookupService.listPurchaseRequest(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)))
      }
    };
  }

  getPurchaseOrders(key = 'purchaseDocumentId'): FilterConfig {
    return {
      key,
      label: 'Purchase Order',
      type: 'options',
      labelAr: 'أمر الشراء',
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'PurchaseOrder',
          category: 'ALL'
        };
        return this.lookupService.listPurchaseOrders(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)));
      }
    };
  }

  getSalesOrders(key = 'salesOrderId'): FilterConfig {
    return {
      key,
      label: 'Sales Order',
      type: 'options',
      labelAr: 'أمر البيع',
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          category: 'ALL'
        };
        return this.lookupService.listSalesOrders(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)));
      }
    };
  }

  getOfferPrices(key = 'basedOnId'): FilterConfig {
    return {
      key,
      label: 'Quotation',
      type: 'options',
      labelAr: 'عرض السعر',
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          category: 'ALL'
        };
        return this.lookupService.listOfferPrices(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)));
      }
    };
  }

  getPurchaseInvoices(key = 'purchaseInvoiceId'): FilterConfig {
    return {
      key,
      label: 'Purchase Invoice',
      type: 'options',
      labelAr: 'فاتورة الشراء',
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'PurchaseInvoice',
          category: 'ALL'
        };
        return this.lookupService.listPurchaseInvoices(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)));
      }
    };
  }

  getDocuments(key = 'documentId'): FilterConfig {
    return {
      key,
      label: "Document",
      type: "options",
      labelAr: "المستند",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: [
            ...(advancedFilter || []),
          ],
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'Documents',
          category: 'ALL'
        };
        return this.lookupService.listDocuments(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)));
      }
    };
  }

  getVendors(key = 'vendorId'): FilterConfig {
    return {
      key,
      label: "Vendor",
      type: "options",
      labelAr: "المورد",
      optionsFnc: (page, size, search, advancedFilter) => {
        const request: PaginatedLookupRequest = {
          term: search,
          advancedFilter: advancedFilter,
          includeDeleted: false,
          pageNumber: page,
          pageSize: size,
          orderBy: [],
          // lookupType: 'Vendors',
          category: 'ALL'
        };
        return this.lookupService.listVendors(request)
          .pipe(map((res: any) => this.mapLookupResponse(res.data)))
      }
    };
  }

  fetchPaymentMethods = (pageNumber: number, pageSize: number, term: string) => {
    const request: PaginatedLookupRequest = {
      term,
      advancedFilter: [],
      includeDeleted: false,
      pageNumber,
      pageSize,
      orderBy: [],
      category: 'ALL',
    };

    return this.lookupService.listPaymentMethods(request).pipe(
      map((res: any) => ({
        ...res,
        data: (res?.data ?? []).map((item: any) => ({
          ...item,
          type: this.resolvePaymentType(item.id, item.nameEn),
          typeAr: item.nameAr ?? this.resolvePaymentType(item.id, item.nameEn),
        })),
      }))
    );
  };

  fetchPaymentMethodById = (
    value: number | string,
    options?: { allowFallback?: boolean }
  ) => {
    const allowFallback = options?.allowFallback ?? true;
    const normalizedValue = String(value ?? '').trim().toLowerCase();
    if (!normalizedValue) {
      return of(null);
    }

    return this.fetchPaymentMethods(1, 100, '').pipe(
      map((res: any) => {
        const selected = (res?.data ?? []).find((item: any) => {
          const idMatch = String(item.id ?? '').trim().toLowerCase() === normalizedValue;
          const typeMatch = String(item.type ?? '').trim().toLowerCase() === normalizedValue;
          const nameMatch = String(item.nameEn ?? '').trim().toLowerCase() === normalizedValue;
          return idMatch || typeMatch || nameMatch;
        });
        if (selected) {
          return selected;
        }

        if (!allowFallback) {
          return null;
        }

        // Fallback for view/edit pages:
        // if the value exists in GetById response but is not present in lookup,
        // keep showing it.
        const fallbackType = this.resolvePaymentType(value);
        return {
          id: typeof value === 'number' ? value : null,
          type: fallbackType,
          typeAr:
            PaymentTypeTranslations[fallbackType as unknown as PaymentType] ?? String(value),
          nameEn: String(fallbackType),
          nameAr:
            PaymentTypeTranslations[fallbackType as unknown as PaymentType] ?? String(value),
        };
      })
    );
  };

  private resolvePaymentType(
    idOrValue: number | string | null | undefined,
    nameEn?: string | null
  ): PaymentType | string {
    const value = String(nameEn ?? idOrValue ?? '').trim();
    const normalized = value.toLowerCase().replace(/\s+/g, '');

    const byId: Record<number, PaymentType> = {
      0: PaymentType.None,
      1: PaymentType.Cash,
      2: PaymentType.Mada,
      3: PaymentType.Visa,
      4: PaymentType.STCPay,
      5: PaymentType.Mastercard,
      6: PaymentType.BankTransfer,
      7: PaymentType.DeliveryApp,
      8: PaymentType.Debit,
      9: PaymentType.Tamara,
      10: PaymentType.Tabby,
      11: PaymentType.MultiPayment,
    };

    if (typeof idOrValue === 'number' && byId[idOrValue]) {
      return byId[idOrValue];
    }

    const byName: Record<string, PaymentType> = {
      none: PaymentType.None,
      cash: PaymentType.Cash,
      mada: PaymentType.Mada,
      visa: PaymentType.Visa,
      stcpay: PaymentType.STCPay,
      mastercard: PaymentType.Mastercard,
      banktransfer: PaymentType.BankTransfer,
      deliveryapp: PaymentType.DeliveryApp,
      debit: PaymentType.Debit,
      tamara: PaymentType.Tamara,
      tabby: PaymentType.Tabby,
      multipayment: PaymentType.MultiPayment,
    };

    return byName[normalized] ?? value;
  }

  // Helper method to map lookup response to unified format
  private mapLookupResponse(data: any[]): any[] {
    return data.map((item: any) => ({
      ...item, // Preserve all original properties (e.g., isDefault for currencies)
      key: item.id,
      label: item.nameEn ?? item.nameAr,
      labelAr: item.nameAr ?? item.nameEn,
      id: item.id,
      nameEn: item.nameEn ?? item.nameAr,
      nameAr: item.nameAr ?? item.nameEn,
      name: item.nameEn ?? item.nameAr, // Some components use 'name' property
      exchangeRate: item?.meta?.exchangeRate ?? item?.exchangeRate,
      symbol: item?.meta?.symbol ?? item?.symbol
    }));
  }

  /**
   * Financial years require preserving fiscal metadata and date boundaries.
   * Keep `meta` and `additionalData` explicitly so consumers can rely on them.
   */
  private mapFinancialYearsResponse(data: any[]): any[] {
    return data.map((item: any) => ({
      ...item,
      key: item.id,
      label: item.nameEn ?? item.nameAr,
      labelAr: item.nameAr ?? item.nameEn,
      id: item.id,
      nameEn: item.nameEn ?? item.nameAr,
      nameAr: item.nameAr ?? item.nameEn,
      name: item.nameEn ?? item.nameAr,
      meta: item.meta,
      additionalData: item.additionalData ?? null
    }));
  }

  private mapAccountLookupResponse(data: any[]): any[] {
    return data.map((item: any) => {
      const metaPrefix = item.meta ? `${item.meta} - ` : '';
      const nameEn = metaPrefix + (item.nameEn ?? item.nameAr);
      const nameAr = metaPrefix + (item.nameAr ?? item.nameEn);
      
      return {
        ...item, // Preserve all original properties (e.g., isDefault for currencies)
        key: item.id,
        label: nameEn,
        labelAr: nameAr,
        id: item.id,
        nameEn: nameEn,
        nameAr: nameAr,
        name: nameEn // Some components use 'name' property
      }
    });
  }

  // Helper method to map ProductsWithUnits response, preserving productId and unitId if they exist
  private mapProductsWithUnitsResponse(data: any[]): any[] {
    return data.map((item: any) => {
      const pickName = (primary?: string, fallback?: string) =>
        primary && primary.trim() !== '' ? primary : fallback;
      
      // Extract unitId from meta or directly from item
      const unitId = item.meta?.unitId ?? item.unitId;
      const productId = item.productId ?? item.id;
      
      // Create a unique ID combining productId and unitId to handle multiple units per product
      const uniqueId = unitId !== undefined && unitId !== null 
        ? `${productId}-${unitId}` 
        : productId;
      
      const mapped = {
        key: uniqueId, // Use combined ID as key for uniqueness
        label: pickName(item.nameEn, item.nameAr),
        labelAr: pickName(item.nameAr, item.nameEn),
        id: uniqueId, // Use combined ID so selection works correctly
        productId: productId, // Keep original product ID separate
        nameEn: pickName(item.nameEn, item.nameAr),
        nameAr: pickName(item.nameAr, item.nameEn),
        name: pickName(item.nameEn, item.nameAr) // Some components use 'name'
      };
      
      // Always set unitId if available
      if (unitId !== undefined && unitId !== null) {
        (mapped as any).unitId = unitId;
      }
      
      // Preserve meta object if it exists (for size, barcode, etc.)
      if (item.meta) {
        (mapped as any).meta = item.meta;
      }
      
      return mapped;
    });
  }

  // Helper methods for fetchFn compatibility (used in HTML templates)
  // These methods return functions compatible with fetchFn signature: (pageNumber, pageSize, term) => Observable
  getBranchesFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getBranches().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getStoresFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getStores().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getCurrenciesFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getCurreies().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getProductCategoriesFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getProductCategories().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getTaxCategoriesFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getTaxCategories().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getTaxExceptionsFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getTaxExceptions().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getUnitsFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getUnits().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getAccountsFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getAccounts().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getAccountCategoriesFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getAccountCategories().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getGlAccountWithCategoriesFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getGlAccountWithCategories().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getJournalTypesFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getJournalTypes().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getShiftCategoriesFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getShiftCategories().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getShiftStatesFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getShiftStates().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getDocumentTypesFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getDocumentTypes().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getShiftsFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getShifts().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getFinancialYearsFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getFinancialYears().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getFinancialStatementItemsFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getFinancialStatementTypes().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getCountriesFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getCountries().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getRolesFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getRoles().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getProductsFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getProducts().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getProductsWithUnitsFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getProductsWithUnits().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getContactsFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getContacts().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getCompaniesFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getCompanies().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getCustomersFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getCustomers().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getVendorsFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getVendors().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  getSalesOrdersFn = (pageNumber = 1, pageSize = 10, term = '', filters?: Filter[]): Observable<any> => {
    return this.getSalesOrders().optionsFnc!(pageNumber, pageSize, term, filters || []);
  }

  fetchPaymentTypes = (pageNumber = 1, pageSize = 10, term = ''): Observable<any> => {
    const request: PaginatedLookupRequest = {
      term,
      advancedFilter: [],
      includeDeleted: false,
      pageNumber,
      pageSize,
      orderBy: [],
      category: 'ALL',
    };

    return this.lookupService.listPaymentMethods(request).pipe(
      map((res: any) => ({
        ...res,
        data: (res?.data ?? []).map((item: any) => ({
          ...item,
          type: this.resolvePaymentType(item.id, item.nameEn),
          typeAr: item.nameAr ?? this.resolvePaymentType(item.id, item.nameEn),
        })),
      }))
    );
  }
}


